/**
 * Navigation Configuration
 * Defines the structure of the navigation menu with sections and role-based access control
 */
import {
  LayoutDashboard,
  Users,
  Calendar,
  BedDouble,
  MapPin,
  UserCog,
  TrendingUp,
  FileBarChart,
  Settings,
  Briefcase,
  Shield,
  Lock,
  UserPlus
} from "lucide-react";
import { PLANS } from "../utils/plans";

/**
 * User roles — hierarchy: Owner > Admin > Staff
 * Admin has Owner-level CRUD/management except Revenue Simulation, Reports,
 * and privilege escalation (see permissions.js).
 */
export const ROLES = {
  OWNER: "owner",
  ADMIN: "admin",
  STAFF: "staff"
};

/**
 * Permission levels
 */
export const PERMISSIONS = {
  READ: "read",
  WRITE: "write",
  FULL: "full" // read + write + delete
};

/**
 * Navigation sections configuration
 *
 * Four groups with uppercase English headers (GENERAL / OPERATIONS / MANAGEMENT /
 * ACCOUNT), each a non-clickable label with its items beneath. Dashboard and
 * Settings are ordinary items under GENERAL / ACCOUNT (they used to be standalone
 * clickable section-titles with an empty `items` array — that pattern is gone).
 *
 * Item fields: icon, label, path, requiredRole, requiredPermission, optional
 * requiredPlan, and optional numeric `badge` (a static count; DashboardLayout can
 * also override it per-path via SidebarNav's `badges` prop — e.g. Reservations
 * shows today's arrivals).
 */
export const NAVIGATION_SECTIONS = [
  {
    // Internal id kept as "dashboard" so DashboardLayout's legacy topbar
    // icon lookup (`section.id === "dashboard"`) keeps resolving. Displayed
    // group header is "General".
    id: "dashboard",
    title: "General",
    icon: LayoutDashboard,
    items: [
      {
        icon: LayoutDashboard,
        label: "Dashboard",
        path: "/dashboard",
        requiredRole: null, // Accessible to all authenticated users
        requiredPermission: PERMISSIONS.READ
      }
    ]
  },
  {
    id: "operations",
    title: "Operations",
    icon: Briefcase,
    items: [
      {
        icon: Calendar,
        label: "Reservations",
        path: "/dashboard/reservations",
        requiredRole: null,
        requiredPermission: PERMISSIONS.READ
      },
      {
        icon: Users,
        label: "Guests",
        path: "/dashboard/guests",
        requiredRole: null, // Accessible to all authenticated users
        requiredPermission: PERMISSIONS.READ
      },
      {
        icon: BedDouble,
        label: "Rooms",
        path: "/dashboard/rooms",
        requiredRole: null,
        requiredPermission: PERMISSIONS.READ
      },
      {
        icon: MapPin,
        label: "Activities",
        path: "/dashboard/activities",
        requiredRole: null,
        requiredPermission: PERMISSIONS.READ
      }
    ]
  },
  {
    id: "management",
    title: "Management",
    icon: Shield,
    requiredRole: ROLES.ADMIN, // Entire section hidden for Staff (Owner + Admin only)
    items: [
      {
        icon: UserPlus,
        label: "Collaborators",
        path: "/dashboard/collaborators",
        requiredRole: ROLES.ADMIN,
        requiredPermission: PERMISSIONS.FULL,
        requiredPlan: PLANS.STARTER
      },
      {
        icon: UserCog,
        label: "Staff",
        path: "/dashboard/staff",
        requiredRole: ROLES.ADMIN,
        requiredPermission: PERMISSIONS.FULL,
        requiredPlan: PLANS.PROFESSIONAL
      },
      {
        icon: TrendingUp,
        label: "Revenue Simulation",
        path: "/dashboard/revenue",
        requiredRole: ROLES.OWNER,
        requiredPermission: PERMISSIONS.READ,
        requiredPlan: PLANS.PROFESSIONAL
      },
      {
        icon: FileBarChart,
        label: "Reports",
        path: "/dashboard/reports",
        requiredRole: ROLES.OWNER,
        requiredPermission: PERMISSIONS.READ,
        requiredPlan: PLANS.PROFESSIONAL
      }
    ]
  },
  {
    // Accessible to ALL roles. The Settings page itself gates its sections
    // (Profile/Property = Owner/Admin; Security + Notifications = everyone),
    // so Staff only see Security + Notifications.
    id: "account",
    title: "Account",
    icon: Settings,
    items: [
      {
        icon: Settings,
        label: "Settings",
        path: "/dashboard/settings",
        requiredRole: null,
        requiredPermission: PERMISSIONS.READ
      }
    ]
  }
];

/**
 * Get all navigation items (flattened)
 */
export const getAllNavigationItems = () => {
  return NAVIGATION_SECTIONS.flatMap(section => section.items);
};

/**
 * Get navigation item by path
 */
export const getNavigationItemByPath = (path) => {
  return getAllNavigationItems().find(item => item.path === path);
};
